/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: VIPListMembersSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Phone, GripVertical } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { VIPListMember } from './types';
import { DEFAULT_MEMBER } from './constants';

interface VIPListMembersSectionProps {
  members: VIPListMember[];
  onChange: (members: VIPListMember[]) => void;
}

interface MemberRowItemProps {
  member: VIPListMember & { __tempId?: number };
  index: number;
  onFieldChange: (index: number, field: keyof VIPListMember, value: string | number | boolean | null) => void;
  onRemove: (index: number) => void;
}

function MemberRowItem({ member, index, onFieldChange, onRemove }: MemberRowItemProps) {
  const { t } = useI18n();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: member.id ?? member.__tempId ?? index
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-12 gap-2 py-1.5 px-2 border border-border rounded-md bg-card ${isDragging ? 'shadow-lg ring-2 ring-primary/40' : ''}`}
    >
      <div className="col-span-12 md:col-span-3 flex items-center gap-1.5">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted/60 rounded flex-shrink-0"
          aria-label={t('common.reorder')}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        <div className="flex-1 flex items-center gap-1.5">
          <Phone className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <Input
            id={`callerIdNumber-${index}`}
            value={member.callerIdNumber}
            onChange={(e) => onFieldChange(index, 'callerIdNumber', e.target.value)}
            placeholder={t('vipLists.form.fields.members.callerIdNumber.placeholder', 'e.g. 5551234567')}
            className="flex-1 h-8 text-xs"
          />
        </div>
      </div>

      <div className="col-span-12 md:col-span-2 flex items-center">
        <Input
          id={`callerIdName-${index}`}
          value={member.callerIdName}
          onChange={(e) => onFieldChange(index, 'callerIdName', e.target.value)}
          placeholder={t('vipLists.form.fields.members.callerIdName.placeholder', 'e.g. John VIP')}
          className="h-8 text-xs w-full"
        />
      </div>

      <div className="col-span-12 md:col-span-2 flex items-center">
        <Input
          id={`priority-${index}`}
          type="number"
          min="1"
          max="100"
          value={member.priority}
          onChange={(e) => onFieldChange(index, 'priority', Number(e.target.value) || 1)}
          placeholder="1"
          className="h-8 text-xs w-full"
        />
      </div>

      <div className="col-span-12 md:col-span-2 flex items-center">
        <Input
          id={`notes-${index}`}
          value={member.notes}
          onChange={(e) => onFieldChange(index, 'notes', e.target.value)}
          placeholder={t('common.empty')}
          className="h-8 text-xs w-full"
        />
      </div>

      <div className="col-span-12 md:col-span-2 flex items-center md:justify-center">
        <Toggle
          id={`enabled-${index}`}
          value={member.enabled}
          onChange={(value) => onFieldChange(index, 'enabled', value)}
        />
      </div>

      <div className="col-span-12 md:col-span-1 flex items-center md:justify-end">
        <SimpleTooltip content={t('common.remove') || 'Remove'}>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    </div>
  );
}

export function VIPListMembersSection({ members, onChange }: VIPListMembersSectionProps) {
  const { t } = useI18n();
  const [memberRows, setMemberRows] = useState<(VIPListMember & { __tempId?: number })[]>(members);
  const onChangeRef = useRef(onChange);
  const nextTempIdRef = useRef(0);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setMemberRows(members.map(member => {
      // Ensure each member has a tempId for sorting if it doesn't have an id
      if (!member.id && !(member as any).__tempId) {
        return { ...member, __tempId: nextTempIdRef.current++ } as VIPListMember & { __tempId: number };
      }
      return member;
    }));
  }, [members]);

  const handleMemberChange = (index: number, field: keyof VIPListMember, value: string | number | boolean | null) => {
    const updated = [...memberRows];
    updated[index] = { ...updated[index], [field]: value };
    setMemberRows(updated);
    onChangeRef.current(updated);
  };

  const handleAddMember = () => {
    const newMember: VIPListMember & { __tempId: number } = {
      ...DEFAULT_MEMBER,
      vipListId: 0,
      __tempId: nextTempIdRef.current++,
      id: undefined as any,
      createdAt: null,
      createdBy: null,
      updatedAt: null,
      updatedBy: null
    };
    const updated = [...memberRows, newMember];
    setMemberRows(updated);
    onChangeRef.current(updated);
  };

  const handleRemoveMember = (index: number) => {
    const updated = memberRows.filter((_, i) => i !== index);
    setMemberRows(updated);
    onChangeRef.current(updated);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setMemberRows((items) => {
        const oldIndex = items.findIndex((member, i) => {
          const id = member.id ?? (member as any).__tempId ?? i;
          return id === active.id || id === Number(active.id);
        });
        const newIndex = items.findIndex((member, i) => {
          const id = member.id ?? (member as any).__tempId ?? i;
          return id === over.id || id === Number(over.id);
        });

        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }

        const reordered = arrayMove(items, oldIndex, newIndex);
        onChangeRef.current(reordered);
        return reordered;
      });
    }
  };

  const sortableIds = memberRows.map((member, i) => member.id ?? member.__tempId ?? i);

  return (
    <FormBox title={t('vipLists.form.sections.members')} icon={<Phone className="h-5 w-5" />} className="mt-6">
      <div className="space-y-4">
        {memberRows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('vipLists.form.members.empty')}
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                <div className="hidden md:grid grid-cols-12 gap-2 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <SimpleTooltip content={t('vipLists.form.fields.members.callerIdNumber.tooltip', 'Caller ID number')} className="col-span-3">
                    <span className="flex items-center gap-2">
                      <GripVertical className="h-3 w-3 opacity-0" />
                      {t('vipLists.form.fields.members.callerIdNumber.label', 'Caller ID')}
                    </span>
                  </SimpleTooltip>
                  <SimpleTooltip content={t('vipLists.form.fields.members.callerIdName.tooltip', 'Caller name')} className="col-span-2">
                    <span className="">
                      {t('vipLists.form.fields.members.callerIdName.label', 'Name')}
                    </span>
                  </SimpleTooltip>
                  <SimpleTooltip content={t('vipLists.form.fields.members.priority.tooltip', 'Priority')} className="col-span-2">
                    <span className="">
                      {t('vipLists.form.fields.members.priority.label', 'Priority')}
                    </span>
                  </SimpleTooltip>
                  <SimpleTooltip content={t('vipLists.form.fields.members.notes.tooltip', 'Notes')} className="col-span-2">
                    <span className="">
                      {t('vipLists.form.fields.members.notes.label', 'Notes')}
                    </span>
                  </SimpleTooltip>
                  <SimpleTooltip content={t('vipLists.form.fields.members.enabled.tooltip')} className="col-span-2">
                    <span className="text-center">
                      {t('vipLists.form.fields.members.enabled.label')}
                    </span>
                  </SimpleTooltip>
                  <span className="col-span-1 text-right">
                    {t('common.actions')}
                  </span>
                </div>
                {memberRows.map((member, index) => (
                  <MemberRowItem
                    key={member.id ?? member.__tempId ?? index}
                    member={member}
                    index={index}
                    onFieldChange={handleMemberChange}
                    onRemove={handleRemoveMember}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="flex justify-end">
          <Button type="button" onClick={handleAddMember} variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            {t('common.add')}
          </Button>
        </div>
      </div>
    </FormBox>
  );
}

